import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { CheckoutReducer } from '@app/pages/checkout-app/checkout-store/reducers';
import { CheckoutActions } from '@app/pages/checkout-app/checkout-store/actions';
import { Store } from '@ngrx/store';
import { ExtensionComponentType, ICheckoutExtensions } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { DynamicRule } from '@pdqdev/pdq-shared/dist/entities/Rules';
import { ViewMode } from '../../../models';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
import { IntegrationsService } from '@services/integrations/integrations.service';
import { TypedRuleGroup } from '@app/sharedModule/components/rules-dynamic-form/model';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { getExtensionRules } from '../../../shared/all-extension-rules';
import { generateRandomString } from '@utils/string-utils';
import {
  DEFAULT_DISCLAIMER_TEXT,
  DEFAULT_INFO_TEXT,
  DEFAULT_FIXED_RATE,
  DEFAULT_INTERNAL_NAME,
  DEFAULT_INTERNAL_DESCRIPTION,
  DEFAULT_TITLE_TEXT,
  OrderProtectionExtension,
  OrderProtectionFormGroup,
  OrderProtectionProductInfo,
  OrderProtectionProductView,
  OrderProtectionTab,
  USAGE_TOKEN_PREFIX,
  USAGE_TOKEN_RANDOM_LENGTH,
  WidgetConfigFormGroup,
} from './model';

const OP_INTEGRATION_NAME = 'order_protection';

@Component({
  selector: 'order-protection',
  templateUrl: './order-protection.component.html',
  styleUrls: ['./order-protection.component.scss'],
  standalone: false,
})
export class OrderProtectionComponent implements OnInit {
  loading$ = this.store.select(CheckoutReducer.selectIsLoading);
  loadedExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  loadedExtension: ICheckoutExtensions;
  tabs = [
    { label: 'Settings', value: OrderProtectionTab.SETTINGS },
    { label: 'Pricing', value: OrderProtectionTab.PRICING },
    { label: 'Appearance', value: OrderProtectionTab.APPEARANCE },
    { label: 'Display Conditions', value: OrderProtectionTab.DISPLAY_CONDITIONS },
  ];
  OrderProtectionTab = OrderProtectionTab;
  activeTab = OrderProtectionTab.SETTINGS;
  formGroup: FormGroup<OrderProtectionFormGroup>;
  displayRules = getExtensionRules();
  integrationConnected = false;
  partnerApplicationId = '';
  productInfo: OrderProtectionProductInfo | null = null;
  productLoading = true;
  productView: OrderProtectionProductView = {
    state: 'loading',
    productId: null,
    imageUrl: null,
    title: null,
    meta: null,
  };

  private destroyRef = inject(DestroyRef);

  constructor(
    private store: Store<CheckoutReducer.State>,
    private extensionsService: ExtensionsService,
    private integrationsService: IntegrationsService,
    private rulesDynamicFormService: RulesDynamicFormService,
  ) {}

  // Save requires OP connected AND product resolved — a widget can't sync/render otherwise.
  get isReady(): boolean {
    return this.integrationConnected && !!this.productInfo?.found;
  }

  ngOnInit() {
    this.extensionsService.setCurrentExtensionType(ExtensionComponentType.ORDER_PROTECTION);

    this.initForm();

    this.formGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.dispatchExampleViewValues();
    });

    this.formGroup.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.updateFormValidity());

    this.formGroup.controls.rateType.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.applyRateBounds();
      this.updateFormValidity();
    });

    this.integrationsService.integrations$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((integrations) => {
      this.integrationConnected = !!this.integrationsService.isConnected(OP_INTEGRATION_NAME);
      this.partnerApplicationId =
        integrations?.[OP_INTEGRATION_NAME]?.instance?.metadata?.orderProtection?.applicationId ?? '';
      this.updateFormValidity();
    });

    this.extensionsService
      .resolveOrderProtectionProduct()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (info) => {
          this.productInfo = info;
          this.productLoading = false;
          this.applyRateBounds();
          this.updateFormValidity();
        },
        error: () => {
          this.productInfo = {
            found: false,
            productId: null,
            title: null,
            imageUrl: null,
            tierCount: 0,
            minPrice: null,
            maxPrice: null,
          };
          this.productLoading = false;
          this.applyRateBounds();
          this.updateFormValidity();
        },
      });

    this.updateFormValidity();

    this.loadedExtension$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((extension: ICheckoutExtensions) => {
      this.loadedExtension = extension;
      if (extension) {
        this.updateFormValues(extension.template, extension.usageToken);
      }
    });

    this.extensionsService.save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.handleSave();
    });
  }

  onTabChange(event: OrderProtectionTab) {
    this.activeTab = event;
  }

  private updateFormValidity() {
    this.productView = this.buildProductView();
    this.extensionsService.setFormValid(this.formGroup.valid && this.isReady);
  }

  // A fixed rate must land on a real protection tier, so bound it to the product's price range.
  private applyRateBounds() {
    const rate = this.formGroup.controls.rate;
    const isFixed = this.formGroup.controls.rateType.value === OrderProtectionExtension.RateType.FIXED;
    const { minPrice, maxPrice } = this.productInfo ?? {};

    const bounded = isFixed && this.productInfo?.found && minPrice != null && maxPrice != null;
    rate.setValidators(
      bounded
        ? [Validators.required, Validators.min(minPrice), Validators.max(maxPrice)]
        : [Validators.required, Validators.min(0)],
    );
    rate.updateValueAndValidity({ emitEvent: false });
  }

  private buildProductView(): OrderProtectionProductView {
    const empty = { productId: null, imageUrl: null, title: null, meta: null };
    if (this.productLoading) {
      return { state: 'loading', ...empty };
    }
    // Product-first: no product means OP isn't installed in Shopify. Surface the connect step only once it exists.
    if (!this.productInfo?.found) {
      return { state: 'not_found', ...empty };
    }
    if (!this.integrationConnected) {
      return { state: 'not_connected', ...empty };
    }

    const { productId, title, imageUrl, minPrice, maxPrice, tierCount } = this.productInfo;
    const range = minPrice != null && maxPrice != null ? `$${minPrice.toFixed(2)} – $${maxPrice.toFixed(2)}` : '';
    const meta = [range, `${tierCount} price tiers`].filter(Boolean).join(' · ');
    return { state: 'found', productId, title, imageUrl, meta };
  }

  private initForm() {
    this.formGroup = new FormGroup<OrderProtectionFormGroup>({
      usageToken: new FormControl(USAGE_TOKEN_PREFIX + generateRandomString(USAGE_TOKEN_RANDOM_LENGTH), {
        nonNullable: true,
      }),
      internalName: new FormControl(DEFAULT_INTERNAL_NAME, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      internalDescription: new FormControl(DEFAULT_INTERNAL_DESCRIPTION, { nonNullable: true }),
      status: new FormControl(OrderProtectionExtension.WidgetStatus.ACTIVE, { nonNullable: true }),
      automaticallyAddToCart: new FormControl(true, { nonNullable: true }),
      rateType: new FormControl(OrderProtectionExtension.RateType.FIXED, { nonNullable: true }),
      rate: new FormControl(DEFAULT_FIXED_RATE, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(0)],
      }),
      config: this.createConfigGroup(),
      restrictionsRules: this.rulesDynamicFormService.initRulesFormArray([], this.displayRules) as FormArray<
        FormGroup<TypedRuleGroup>
      >,
    });

    this.dispatchExampleViewValues();
  }

  private createConfigGroup(config?: OrderProtectionExtension.WidgetConfig): FormGroup<WidgetConfigFormGroup> {
    return new FormGroup<WidgetConfigFormGroup>({
      titleText: new FormControl(config?.titleText ?? DEFAULT_TITLE_TEXT, { nonNullable: true }),
      titleTextBold: new FormControl(config?.titleTextBold ?? false, { nonNullable: true }),
      titleTextSize: new FormControl(config?.titleTextSize ?? OrderProtectionExtension.TextSize.BASE, {
        nonNullable: true,
      }),
      infoText: new FormControl(config?.infoText ?? DEFAULT_INFO_TEXT, { nonNullable: true }),
      infoTextSize: new FormControl(config?.infoTextSize ?? OrderProtectionExtension.TextSize.BASE, {
        nonNullable: true,
      }),
      showInfoModal: new FormControl(config?.showInfoModal ?? true, { nonNullable: true }),
      actionButtonType: new FormControl(config?.actionButtonType ?? OrderProtectionExtension.ActionType.TOGGLE, {
        nonNullable: true,
      }),
      hideLogo: new FormControl(config?.hideLogo ?? false, { nonNullable: true }),
      showDisclaimerText: new FormControl(config?.showDisclaimerText ?? false, { nonNullable: true }),
      disclaimerTextOn: new FormControl(config?.disclaimerTextOn ?? DEFAULT_DISCLAIMER_TEXT, { nonNullable: true }),
      disclaimerTextOnSize: new FormControl(config?.disclaimerTextOnSize ?? OrderProtectionExtension.TextSize.SMALL, {
        nonNullable: true,
      }),
      showBorder: new FormControl(config?.showBorder ?? false, { nonNullable: true }),
      borderStyle: new FormControl(config?.borderStyle ?? OrderProtectionExtension.BorderStyle.SOLID, {
        nonNullable: true,
      }),
      borderWidth: new FormControl(config?.borderWidth ?? OrderProtectionExtension.BorderWidth.BASE, {
        nonNullable: true,
      }),
      borderRadius: new FormControl(config?.borderRadius ?? OrderProtectionExtension.BorderRadius.BASE, {
        nonNullable: true,
      }),
      backgroundColor: new FormControl(
        config?.backgroundColor ?? OrderProtectionExtension.BackgroundColor.TRANSPARENT,
        {
          nonNullable: true,
        },
      ),
    });
  }

  private updateFormValues(loadedValues: OrderProtectionExtension.Template, usageToken?: string) {
    this.formGroup.patchValue({
      usageToken: usageToken ?? this.formGroup.controls.usageToken.value,
      internalName: loadedValues.internalName ?? DEFAULT_INTERNAL_NAME,
      internalDescription: loadedValues.internalDescription ?? DEFAULT_INTERNAL_DESCRIPTION,
      status: loadedValues.widget?.status ?? OrderProtectionExtension.WidgetStatus.ACTIVE,
      automaticallyAddToCart: loadedValues.pricing?.automaticallyAddToCart ?? true,
      rateType: loadedValues.pricing?.rateType ?? OrderProtectionExtension.RateType.FIXED,
      rate: loadedValues.pricing?.rate ?? DEFAULT_FIXED_RATE,
    });

    this.formGroup.setControl('config', this.createConfigGroup(loadedValues.widget?.config));
    this.formGroup.setControl(
      'restrictionsRules',
      this.rulesDynamicFormService.initRulesFormArray(
        loadedValues.restrictionsRules ?? [],
        this.displayRules,
      ) as FormArray<FormGroup<TypedRuleGroup>>,
    );

    this.dispatchExampleViewValues();
  }

  private dispatchExampleViewValues() {
    const mode = this.loadedExtension ? ViewMode.EDIT : ViewMode.NEW;
    const data = {
      [ExtensionComponentType.ORDER_PROTECTION]: {
        mode,
        template: this.buildTemplate(),
      },
    };
    this.store.dispatch(CheckoutActions.liveExampleTemplateView({ data }));
  }

  private buildTemplate(): OrderProtectionExtension.Template {
    const {
      internalName,
      internalDescription,
      status,
      automaticallyAddToCart,
      rateType,
      rate,
      config,
      restrictionsRules,
    } = this.formGroup.getRawValue();
    return {
      internalName,
      internalDescription,
      restrictionsRules: restrictionsRules as unknown as DynamicRule[],
      widget: {
        name: internalName,
        status,
        config,
      },
      pricing: {
        type: OrderProtectionExtension.PolicyType.SHIPPING,
        automaticallyAddToCart,
        rateType,
        rate,
      },
      protectionProductId: this.productInfo?.productId ?? undefined,
    };
  }

  private handleSave() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }
    if (!this.isReady) return;

    const payload: Partial<ICheckoutExtensions> = {
      template: this.buildTemplate(),
      usageToken: this.formGroup.controls.usageToken.value,
      type: ExtensionComponentType.ORDER_PROTECTION,
    };

    if (this.loadedExtension?.id) {
      const id = this.loadedExtension.id;
      this.store.dispatch(CheckoutActions.updateExtension({ id, extension: payload }));
    } else {
      this.store.dispatch(CheckoutActions.saveExtension({ extension: payload }));
    }
  }
}
